import React, { useContext } from "react";
import useSWR from "swr";
import { useLocation, useHistory, useParams, Switch, Route, Redirect } from "react-router-dom";
import Loader from "Components/loader/Loader";
import { displayFormattedDate } from "Utils/dateFormats";
import { Icon, disabledDomain } from "Icons/index";
import t, { getAccountTypeTranslation } from "Utils/translation.js";
import ButtonBack from "Components/buttonBack/ButtonBack";
import DetailInfo from "Components/detailInfo/DetailInfo";
import Dashboard from "./tabs/Dashboard";
import Domains from "./tabs/Domains";
import Details from "./tabs/Details";
import VolumeManagement from "./tabs/VolumeManagement";
import ServiceManagement from "./tabs/ServiceManagement";
import cx from "classnames";
import s from "../delivery.scss";
import { AuthContext } from "@/App";
import ShowInfoButton from "Components/showInfoButton/ShowInfoButton";
import useToggleDetailsInfo from "Utils/useToggleDetailsInfo";
import DeliveryStatusLabel from "Components/deliveryStatusLabel/DeliveryStatusLabel";
import WarningBanner from "Components/warningBanner/WarningBanner";
import { MediaQueriesContext } from "@/App";

const DeliveryAccountShowScreen = function (props) {
  const location = useLocation();
  const history = useHistory();
  const { accountCode } = useParams();
  const { isLoggedUserAdmin, getLoggedUser } = useContext(AuthContext);
  const { isInfoVisible, isInfoToggled, toggleInfo } = useToggleDetailsInfo();

  const { data, error, mutate } = useSWR("/delivery/accounts/" + accountCode);
  const { isMediaMobile } = useContext(MediaQueriesContext);

  if (error && error.status === 404) {
    history.replace("/not-found");
  }

  const account = data && data.resources ? data.resources[0] : null;
  const loggedUser = getLoggedUser();
  const isManager = loggedUser.type === "Manager";

  let activeTab = location.pathname.split("/").slice(-1)[0];

  if (!account || error) {
    return <Loader {...props} />;
  }

  const showTab = (slug) => {
    const path = location.pathname.split("/");
    path[path.length - 1] = slug;
    history.push(path.join("/"));
  };

  const goBack = () => {
    const {
      location: { state },
    } = history;

    if (state && state.fromList) {
      history.goBack();
    } else {
      history.push(`/delivery/accounts/`);
    }
  };

  const basePath = isLoggedUserAdmin()
    ? "/customers/:customerCode/delivery/accounts/:accountCode"
    : "/delivery/accounts/:accountCode";

  return (
    <div className="flex flex-col w-full px-[14px] pb-4">
      <div className="w-full">
        <div className={s.navigation}>
          <ButtonBack onClick={goBack} />
        </div>

        <div className={s.header}>
          <div className={s.titleContainer}>
            <div className="sectionTitle">{account.description}</div>
            {!isMediaMobile && <DeliveryStatusLabel account={account} isManager={isManager} className="ml-3" />}
          </div>

          <ShowInfoButton onClick={toggleInfo} isInfoOpen={isInfoToggled} />
          {!isMediaMobile && (
            <div className={cx(s.subtitle, "sectionDesc", s.accountShowDetails)}>
              {t("delivery.accounts.show.pageLabel.username")}: {account.username}
            </div>
          )}

          {account.status_detail === "wait_pay" ? (
            <div className="col-span-2 mt-3">
              <WarningBanner
                color="yellow"
                msg={
                  isManager
                    ? t("delivery.account.status.waitingCustomerApprovalFull")
                    : t("delivery.accounts.label.waiting_payment")
                }
              />
            </div>
          ) : account.delete_on ? (
            <div className="col-span-2 mt-3">
              <WarningBanner color="yellow" msg={t("delivery.accounts.label.deletingOn")} />
            </div>
          ) : (
            account.domains_count === 0 && (
              <div className="col-span-2 mt-3">
                <WarningBanner color="light-red" msg={t("delivery.accounts.show.warning.noDomains.msg")} />
              </div>
            )
          )}

          {isInfoVisible && (
            <div className={cx(s.info, "mt-2")}>
              {isMediaMobile && (
                <DeliveryStatusLabel
                  className="px-3 mb-2"
                  msg={
                    account.status ? t("delivery.domains.show.label.active") : t("delivery.domains.show.label.inactive")
                  }
                  color={account.status ? "green" : "yellow"}
                />
              )}
              <DetailInfo label="ID" info={account.id} inLine />
              {isMediaMobile && (
                <DetailInfo label={t("delivery.accounts.show.pageLabel.username")} info={account.username} inLine />
              )}
              <DetailInfo
                label={t("detailInfo.label.created")}
                info={account.created_at && displayFormattedDate(account.created_at)}
                inLine
              />

              {account.manager_fullname && (
                <DetailInfo label={t("detailInfo.label.manager")} info={account.manager_fullname || "-"} inLine />
              )}

              <DetailInfo label={t("detailInfo.label.type")} info={getAccountTypeTranslation(account.type)} inLine />
            </div>
          )}
        </div>
      </div>

      <div className={s.settings}>
        <div className={s.scrollTabs}>
          <div className={s.tabs}>
            <div
              className={cx(s.itemTab, { [s.active]: activeTab === "dashboard" })}
              onClick={() => showTab("dashboard")}
            >
              Dashboard
            </div>
            <div className={cx(s.itemTab, { [s.active]: activeTab === "domains" })} onClick={() => showTab("domains")}>
              {t("tab.domains")}
            </div>
            <div
              className={cx(s.itemTab, {
                [s.active]: activeTab === "details",
              })}
              onClick={() => showTab("details")}
            >
              {t("tab.detailsAndSecurity")}
            </div>

            {isLoggedUserAdmin() && (
              <div
                className={cx(s.itemTab, { [s.active]: activeTab === "volume-management" })}
                onClick={() => showTab("volume-management")}
              >
                {t("tab.volumeManagement")}
              </div>
            )}

            <div
              className={cx(s.itemTab, { [s.active]: activeTab === "service-management" })}
              onClick={() => showTab("service-management")}
            >
              {t("tab.serviceManagement")}
            </div>
          </div>
        </div>
      </div>

      {account && (
        <>
          <div className="flex-1 px-4 mb-12 overflow-y-scroll md:mb-0">
            <Switch>
              <Route
                exact
                path={`${basePath}/dashboard`}
                render={() => <Dashboard isLoggedUserAdmin={isLoggedUserAdmin()} accountData={account} />}
              />
              <Route
                exact
                path={`${basePath}/domains`}
                render={() => (
                  <Domains
                    accountId={account.id}
                    accountData={account}
                    domains={account.domains}
                    customerCode={account.customer_code}
                    onDomainsUploaded={() => mutate()}
                    loggedUser={loggedUser}
                  />
                )}
              />
              <Route
                exact
                path={`${basePath}/domains/add`}
                render={() => (
                  <Domains
                    accountId={account.id}
                    accountData={account}
                    domains={account.domains}
                    customerCode={account.customer_code}
                    isAdding
                    loggedUser={loggedUser}
                  />
                )}
              />
              <Route
                exact
                path={`${basePath}/details`}
                render={() => (
                  <Details accountData={account} loggedUser={loggedUser} onDetailsUpdated={() => mutate()} />
                )}
              />

              <Route
                exact
                path={`${basePath}/service-management`}
                render={() => <ServiceManagement accountData={account} refresh={mutate} />}
              />

              <Route
                exact
                path={`${basePath}/volume-management`}
                render={() => <VolumeManagement accountData={account} refresh={mutate} />}
              />

              <Redirect from={basePath} to={`${basePath}/dashboard`} />
            </Switch>
          </div>

          {account.status === "disabled" && (
            <div className={s.wrapFulldMsg}>
              <div className={s.illustrationMsg}>
                <Icon svg={disabledDomain} width="10rem" height="10rem" />
                <div>{t("alert.cannotEditWhileDisabled")}</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DeliveryAccountShowScreen;
